<template>
  <v-card>
    <v-card-title>
      <h2 class="modal-title">{{ $t('base.add_group') }}</h2>
    </v-card-title>
    <v-card-content>
      <v-input-r
        :label="$t('base.name')"
        :hint="errorMessage('name')"
        :invalid="!!errorMessage('name')"
        @input="clearError('name')"
        v-model="form.name"
      />
      <v-input-r
        :label="$t('base.animal_count')"
        :hint="errorMessage('animal_count')"
        :invalid="!!errorMessage('animal_count')"
        @input="clearError('animal_count')"
        v-model="form.animal_count"
        type="number"
      />
      <v-type-select
        v-model="form.typeName"
        :label="$t('base.animal_type')"
        :hint="errorMessage('type')"
        :invalid="!!errorMessage('type')"
        :custom-types="filteredCustomTypes"
        :default-types="filteredDefaultTypes"
        @select="onSelectType"
        @create-enabled="onCreateEnabled"
      />
    </v-card-content>
    <v-card-actions>
      <v-btn
        :loading="loading"
        :disabled="!isFormFilled"
        @click="onCreate"
      >
        {{ $t('base.add') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import api from '@/api'
import { mapActions, mapGetters } from 'vuex'
import formErrorsMixin from '@/mixins/form-errors.mixin.js'

export default {
  name: 'CreateGroupModal',

  mixins: [formErrorsMixin],

  data: () => ({
    form: {
      name: null,
      animal_count: null,
      type: null,
      typeName: ''
    },
    errors: {
      name: null,
      animal_count: null,
      type: null
    },
    loading: false,
    selectedType: null,
    isDefaultType: false,
    createTypeEnabled: false,
    creatingNewType: false
  }),

  mounted () {
    this.loadData()
  },

  computed: {
    ...mapGetters('animaltypes', [
      'animalTypes',
      'defaultAnimalTypes'
    ]),

    filteredCustomTypes () {
      return this.animalTypes.filter(type => !type.deleted)
    },

    filteredDefaultTypes () {
      const customNames = this.animalTypes
        .filter(type => !type.deleted)
        .map(t => t.name.toLowerCase())

      return this.defaultAnimalTypes.filter(defaultType =>
        !customNames.includes(defaultType.name.toLowerCase())
      )
    },

    isFormFilled () {
      return !!this.form.name && !!this.form.animal_count && !!this.form.typeName
    }
  },

  methods: {
    ...mapActions('animaltypes', [
      'loadAnimalTypes',
      'loadDefaultAnimalTypes',
      'createAnimalType'
    ]),

    async loadData () {
      const params = {
        farm: this.$route.params.farm_id,
        deleted: 'false',
        limit: 2000
      }
      await this.loadAnimalTypes(params)
      await this.loadDefaultAnimalTypes()
    },

    onSelectType ({ type, isDefault }) {
      this.selectedType = type
      this.isDefaultType = isDefault
      this.form.type = type.id
      this.createTypeEnabled = true
      this.clearError('type')
    },

    onCreateEnabled (enabled) {
      this.createTypeEnabled = enabled
      if (!enabled) {
        this.selectedType = null
        this.form.type = null
      }
      this.clearError('type')
    },

    checkValidData () {
      const errorCodes = { REQUIRED: '4', MAX_LENGTH: '6' }
      let valid = true

      this.errors = {
        name: null,
        animal_count: null,
        type: null
      }

      if (!this.form.name) {
        this.errors.name = [errorCodes.REQUIRED]
        valid = false
      } else if (this.form.name.length > 100) {
        this.errors.name = [errorCodes.MAX_LENGTH, 100]
        valid = false
      }

      if (!this.form.animal_count) {
        this.errors.animal_count = [errorCodes.REQUIRED]
        valid = false
      }

      if (!this.form.typeName) {
        this.errors.type = [errorCodes.REQUIRED]
        valid = false
      }

      return valid
    },

    async onCreate () {
      this.loading = true

      if (!this.checkValidData()) {
        this.loading = false
        return
      }

      let typeId = this.form.type

      if (!this.selectedType || this.isDefaultType) {
        this.creatingNewType = true
        const typePayload = {
          farm: this.$route.params.farm_id,
          name: this.form.typeName
        }
        const typeError = await this.createAnimalType(typePayload)

        if (typeError) {
          this.$toasted.error(this.$t('errors.incorrect_data'))
          this.loading = false
          this.creatingNewType = false
          return
        }

        await this.loadData()

        const newType = this.animalTypes.find(t =>
          t.name.toLowerCase() === this.form.typeName.toLowerCase()
        )

        if (newType) {
          typeId = newType.id
        } else {
          this.$toasted.error(this.$t('errors.type_not_created'))
          this.loading = false
          this.creatingNewType = false
          return
        }

        this.creatingNewType = false
      }

      const payload = {
        farm: this.$route.params.farm_id,
        animal_type: {
          id: typeId
        },
        name: this.form.name,
        animal_count: this.form.animal_count
      }

      const { errors } = await api.groups.create(payload)

      if (errors) {
        this.errors = {
          name: null,
          animal_count: null,
          type: null,
          ...errors
        }
      } else {
        this.$emit('update')
        this.$emit('close')
      }

      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card__title {
  padding: 2rem 2.4rem 1.6rem;
}

.modal-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.v-card__content {
  border-top: .1rem solid #d7d7d7;
  padding-bottom: 1.5rem !important;
}

.v-card__actions {
  padding: 0 2.4rem 2.4rem;

  button {
    width: 100%;
  }
}
</style>